import { useMemo } from "react";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { defaultConfig } from "../../utils/config";
import ButtonLink from "../../ui/components/ButtonLink";

const Container = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 0;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    margin: 0 -6px;
`;

type PageType = 'Game' | 'Art' | 'Animate';

interface AltPagesButtonProps {
    type: PageType;
    colorMode: 'light' | 'dark';
    isMobile: boolean;
}

const AltPagesButton = ({ type, colorMode, isMobile }: AltPagesButtonProps) => {
    const navigate = useNavigate();

    // 1. Define available types based on the config flag
    const allTypes = useMemo(() => {
        const types: PageType[] = ['Game', 'Art'];
        if (defaultConfig.ENABLE_ANIMATION && defaultConfig.ANIMATION_SHOW_NAV_OPTION) {
            types.push('Animate');
        }
        return types;
    }, []);
    
    // 2. Filter out the current type
    const otherTypes = allTypes.filter(t => t !== type);

    const visibleTypes = useMemo(() => {
        // Only apply randomization on the Game route when on mobile
        if (isMobile && type === 'Game') {
            const randomIndex = Math.floor(Math.random() * otherTypes.length);
            return [otherTypes[randomIndex]];
        }

        // Standard behavior for desktop or non-game routes
        const countToShow = isMobile ? 1 : 2;
        return otherTypes.slice(0, countToShow);
    }, [type, isMobile, otherTypes]);

    // Helper to get route based on type
    const getPath = (t: PageType) => {
        if (t === 'Game') return '/';
        if (t === 'Art') return '/art';
        if (t === 'Animate') return '/animate';
        return '/';
    };

    const getButtonText = (type: PageType) => {
        if (type === 'Game') return 'Game AI';
        if (type === 'Art') return 'Art AI';
        if (type === 'Animate') return 'Animate AI';
        return 'Game AI';
    };

    return (
        <Container>
            {visibleTypes.map((visibleType) => (
                <Link 
                    key={visibleType} 
                    to={getPath(visibleType)} 
                    style={{ textDecoration: 'none' }}
                >
                    <ButtonContainer>
                        <ButtonLink
                            size="small"
                            color={colorMode === 'dark' ? 'white' : 'black'}
                            text={getButtonText(visibleType)}
                            icon={'right'}
                            iconPos={'right'}
                            onClick={() => navigate(getPath(visibleType))}
                        />
                    </ButtonContainer>   
                </Link>
            ))}
        </Container>
    );
};

export default AltPagesButton;